import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import QuizzGame from '@/features/quiz/QuizzGame.jsx';
import { QuizzAuthForm } from '@/features/quiz/QuizzAuthForm.jsx';
import { useQuiz } from '@/hooks/useQuiz.js';
import { LoadingScreen } from '@/components/LoadingScreen.jsx';

const getTier = (points) => {
    if (points >= 1400) return { label: 'Masterclass', emoji: '👑', color: 'text-yellow-400' };
    if (points >= 800)  return { label: 'Premier de cordée', emoji: '🎯', color: 'text-action-green' };
    if (points >= 300)  return { label: 'Titulaire solide', emoji: '⚽️', color: 'text-blue-400' };
    return { label: 'Niveau Footix', emoji: '🍿', color: 'text-gray-500' };
};

export const StepQuizz = ({ contestId, onFinish, isAnonymous = true, hasZone = false, zones = [] }) => {
    const { t } = useTranslation();
    const { quiz, loading, result, submitting, needsAuth, error, participate } = useQuiz(contestId);
    const [played, setPlayed] = useState(false);
    const [showProfiling, setShowProfiling] = useState(false);
    const answersRef = useRef(null);

    const handleFinish = () => {
        if (onFinish) onFinish();
        else window.history.back();
    };

    // Profilage zone demandé seulement si l'orga a des zones configurées et que le user ne l'a pas donnée
    const needsZone = zones.length > 0 && !hasZone;

    // Fin du quiz : écran profilage si identité requise (anonyme) ou zone à capturer ; sinon direct.
    const handleComplete = async (answers) => {
        answersRef.current = answers;
        setPlayed(true);
        if (isAnonymous || needsZone) {
            setShowProfiling(true);
            return;
        }
        await participate(answers);
    };

    // Soumission du profilage : on rejoue avec { zone, email?, pseudo?, captcha_token? }
    const handleProfilingSubmit = async (data) => {
        await participate(answersRef.current, data);
    };

    if (loading || (!quiz && !played)) return <LoadingScreen />;

    // --- PROFILAGE (zone + mur de conversion si anonyme) ---
    if ((showProfiling || needsAuth) && !result) {
        return (
            <QuizzAuthForm
                onSubmit={handleProfilingSubmit}
                isLoading={submitting}
                error={error}
                isAnonymous={isAnonymous}
                zones={zones}
            />
        );
    }

    // --- CALCUL EN COURS ---
    if (played && submitting) {
        return (
            <div className="fixed inset-0 z-50 bg-brand-dark flex flex-col items-center justify-center gap-6 font-sans">
                <motion.div
                    animate={{ scale: [1, 1.2, 1], opacity: [0.6, 1, 0.6] }}
                    transition={{ repeat: Infinity, duration: 1.4, ease: 'easeInOut' }}
                    className="text-5xl"
                >
                    ⚡️
                </motion.div>
                <p className="text-[10px] font-bold text-gray-500 uppercase tracking-widest">
                    {t('quiz.result.computing', 'Calcul du score...')}
                </p>
            </div>
        );
    }

    // --- RÉSULTAT ---
    if (result) {
        const correctCount = result.answers.filter(a => a.isCorrect === true).length;
        const tier = getTier(result.totalScore);

        return (
            <div className="fixed inset-0 z-50 bg-brand-dark font-sans overflow-hidden flex flex-col select-none">
                <style>{`
                    :root { --ease-out: cubic-bezier(0.16, 1, 0.3, 1); }
                    .slide-up { animation: slideUp 0.7s var(--ease-out) forwards; opacity: 0; transform: translateY(24px); }
                    .zoom-in { animation: zoomIn 0.6s var(--ease-out) forwards; opacity: 0; transform: scale(0.85); }
                    @keyframes slideUp { to { opacity: 1; transform: translateY(0); } }
                    @keyframes zoomIn  { to { opacity: 1; transform: scale(1); } }
                    .delay-1 { animation-delay: 0.1s; }
                    .delay-2 { animation-delay: 0.2s; }
                    .delay-3 { animation-delay: 0.35s; }
                    .delay-4 { animation-delay: 0.5s; }
                `}</style>

                {/* Halo fond */}
                <div className="absolute inset-0 pointer-events-none overflow-hidden">
                    <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-96 h-96 rounded-full bg-action-green/10 blur-[80px]" />
                    <div className="absolute bottom-0 right-0 w-72 h-72 rounded-full bg-yellow-400/5 blur-[80px]" />
                </div>

                <div className="relative z-10 flex-1 overflow-y-auto px-5 pb-8 pt-10 flex flex-col">

                    {/* HEADER */}
                    <div className="text-center mb-8 slide-up">
                        <p className="text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-1">
                            GOJO<span className="text-action-green">.</span> QI FOOT
                        </p>
                        <p className="text-[4.5rem] font-black text-white tabular-nums leading-none tracking-tighter">
                            {result.totalScore.toLocaleString()}
                        </p>
                        <p className="text-xs font-bold text-gray-500 uppercase tracking-widest mt-1">pts</p>
                    </div>

                    {/* TIER */}
                    <div className="flex justify-center mb-6 zoom-in delay-1">
                        <div className="flex items-center gap-2.5 px-5 py-2.5 rounded-full bg-white/5 border border-white/10 backdrop-blur-sm">
                            <span className="text-xl">{tier.emoji}</span>
                            <span className={`text-sm font-black uppercase tracking-tight ${tier.color}`}>
                                {tier.label}
                            </span>
                        </div>
                    </div>

                    {/* STATS */}
                    <div className="grid grid-cols-3 gap-2 mb-4 slide-up delay-2">
                        {[
                            { value: correctCount, label: t('quiz.result.correct', 'Correctes'), color: 'text-action-green' },
                            { value: result.answers.length - correctCount, label: t('quiz.result.incorrect', 'Manquées'), color: 'text-red-400' },
                            { value: result.answers.length, label: t('quiz.result.total', 'Questions'), color: 'text-gray-400' },
                        ].map(({ value, label, color }) => (
                            <div key={label} className="bg-white/5 border border-white/10 rounded-2xl p-3 text-center">
                                <p className={`text-2xl font-black ${color}`}>{value}</p>
                                <p className="text-[9px] font-bold text-gray-500 uppercase tracking-widest mt-0.5">{label}</p>
                            </div>
                        ))}
                    </div>

                    {/* BONUS PRONO */}
                    {result.totalScore > 0 && (
                        <div className="slide-up delay-2 mb-4 flex items-center gap-3 bg-action-green/10 border border-action-green/20 rounded-2xl px-4 py-3">
                            <span className="text-xl">🚀</span>
                            <div>
                                <p className="text-xs font-black text-action-green uppercase tracking-wide">
                                    {t('quiz.result.bonus_title', 'Bonus prono activé !')}
                                </p>
                                <p className="text-[11px] text-gray-400 font-semibold mt-0.5">
                                    {t('quiz.result.bonus_desc', '+{{pts}} pts s\'ajoutent à ton classement', {
                                        pts: result.totalScore.toLocaleString()
                                    })}
                                </p>
                            </div>
                        </div>
                    )}

                    {/* DÉTAIL QUESTIONS */}
                    <div className="slide-up delay-3 mb-6">
                        <p className="text-[9px] font-bold text-gray-600 uppercase tracking-widest mb-3">
                            {t('quiz.result.detail', 'Détail des réponses')}
                        </p>
                        <div className="flex flex-col gap-2">
                            {result.answers.map((answer, i) => {
                                const isCorrect = answer.isCorrect === true;
                                const isWrong   = answer.isCorrect === false;
                                const isTimeout = answer.isCorrect === null;
                                const question  = quiz?.questions.find(q => q.id === answer.questionId);
                                const correctText = question?.choices.find(c => c.id === answer.correctChoiceId)?.text;
                                const chosenText  = question?.choices.find(c => c.id === answer.chosenChoiceId)?.text;

                                return (
                                    <motion.div
                                        key={answer.questionId}
                                        initial={{ opacity: 0, x: -10 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ delay: 0.5 + i * 0.07, duration: 0.3 }}
                                        className={`rounded-2xl border px-4 py-3 ${
                                            isCorrect ? 'bg-action-green/10 border-action-green/20'
                                            : isWrong  ? 'bg-red-500/10 border-red-500/20'
                                            : 'bg-white/5 border-white/10'
                                        }`}
                                    >
                                        <div className="flex items-start justify-between gap-3">
                                            <div className="flex items-start gap-2.5 flex-1 min-w-0">
                                                <span className={`w-6 h-6 shrink-0 rounded-full flex items-center justify-center text-xs font-black ${
                                                    isCorrect ? 'bg-action-green text-white'
                                                    : isWrong  ? 'bg-red-500 text-white'
                                                    : 'bg-white/10 text-gray-400'
                                                }`}>
                                                    {isCorrect ? '✓' : isWrong ? '✕' : '–'}
                                                </span>
                                                <p className="text-sm font-bold text-white leading-snug">
                                                    {question?.text}
                                                    {isTimeout && <span className="ml-1.5 text-gray-500 font-normal">⏱</span>}
                                                </p>
                                            </div>
                                            {isCorrect && (
                                                <span className="text-sm font-black text-action-green shrink-0">
                                                    +{answer.pointsEarned.toLocaleString()}
                                                </span>
                                            )}
                                        </div>

                                        {!isTimeout && (
                                            <div className="mt-2 pl-[2.125rem]">
                                                {isWrong && chosenText && (
                                                    <p className="text-xs text-red-400 line-through opacity-60">{chosenText}</p>
                                                )}
                                                {correctText && (
                                                    <p className={`text-sm font-semibold ${isCorrect ? 'text-action-green' : 'text-gray-300'}`}>
                                                        {correctText}
                                                    </p>
                                                )}
                                            </div>
                                        )}
                                    </motion.div>
                                );
                            })}
                        </div>
                    </div>

                    {/* CTA */}
                    <div className="slide-up delay-4 mt-auto">
                        <button
                            onClick={handleFinish}
                            className="w-full py-4 bg-action-green text-white font-black text-base uppercase tracking-widest rounded-xl shadow-[0_10px_30px_-10px_rgba(16,185,129,0.5)] hover:scale-[1.02] active:scale-95 transition-all"
                        >
                            {t('quiz.result.back_cta', 'Retour au prono')}
                        </button>
                    </div>

                </div>
            </div>
        );
    }

    return <QuizzGame questions={quiz.questions} onComplete={handleComplete} onBack={handleFinish} />;
};
